// Type definitions
interface ArticleListedProps {
  container: HTMLElement;
  paging?: boolean;
  pagingBy?: number;
  fadeIn?: number;
  fadeOut?: number;
  magazine?: string;
  forcePaging?: boolean;
  showRecentArticles?: boolean;
  mobilePageLimit?: number; // Number of pages to show on mobile
  mobileBreakpoint?: number; // Mobile breakpoint in pixels
  onInit?: () => void;
  onPageChange?: (direction: string, page: number | string) => void;
}

interface ArticleState {
  currentPage: number;
  previousPage: number | null;
  isLoading: boolean;
  totalPages: number;
  container: HTMLElement;
  pagingHolders: HTMLElement[];
  isMobileView: boolean;
}

export const createArticleListed = (props: ArticleListedProps) => {
  // Set default options
  const options = {
    paging: props.paging !== undefined ? props.paging : true,
    pagingBy: props.pagingBy || 11,
    fadeIn: props.fadeIn !== undefined ? props.fadeIn : 500,
    fadeOut: props.fadeOut !== undefined ? props.fadeOut : 100,
    magazine: props.magazine || 'isport',
    forcePaging: props.forcePaging !== undefined ? props.forcePaging : false,
    showRecentArticles: props.showRecentArticles !== undefined ? props.showRecentArticles : true,
    mobilePageLimit: props.mobilePageLimit || 5, // Default to 5 pages on mobile
    mobileBreakpoint: props.mobileBreakpoint || 679, // Default mobile breakpoint
  };

  // Get all chapters including the related content
  const allChapters = Array.from(props.container.querySelectorAll('ul.main > li')) as HTMLElement[];

  // Find if there's a dedicated related chapter (with data-id="related")
  const relatedChapterIndex = allChapters.findIndex(
    (chapter) => chapter.getAttribute('data-id') === 'related'
  );

  // Calculate how many regular chapters we have (excluding related content if it exists)
  const regularChaptersCount =
    relatedChapterIndex !== -1 ? allChapters.length - 1 : allChapters.length;

  // Parse URL parameters to get initial chapter
  const urlParams = new URLSearchParams(window.location.search);
  const kapitolaParam = urlParams.get('kapitola');

  // Determine initial page based on URL parameters
  let initialPage = 1; // Default to first page

  if (kapitolaParam === 'related') {
    // If URL parameter is 'related', show related content
    initialPage = relatedChapterIndex !== -1 ? relatedChapterIndex + 1 : 1;
  } else if (kapitolaParam) {
    // Try to find chapter by ID for other URL parameters
    const chapterIndex = allChapters.findIndex(
      (chapter) => chapter.getAttribute('data-id') === kapitolaParam
    );
    if (chapterIndex !== -1) {
      initialPage = chapterIndex + 1;
    } else {
      // Try parsing as number if not found by ID
      const kapitolaNumber = parseInt(kapitolaParam, 10);
      if (!isNaN(kapitolaNumber) && kapitolaNumber > 0 && kapitolaNumber <= allChapters.length) {
        initialPage = kapitolaNumber;
      }
    }
  }

  // State management
  const state: ArticleState = {
    currentPage: initialPage,
    previousPage: null,
    isLoading: false,
    totalPages: allChapters.length,
    container: props.container,
    pagingHolders: [],
    isMobileView: window.innerWidth <= options.mobileBreakpoint,
  };

  // Check viewport size and update state if needed
  const checkViewportSize = () => {
    const isMobile = window.innerWidth <= options.mobileBreakpoint;
    state.isMobileView = isMobile;
  };

  // Set up resize observer to update pagination on viewport changes
  const setupResizeObserver = () => {
    // Check if ResizeObserver is available
    if (typeof ResizeObserver === 'undefined') {
      // Fallback to window resize event if ResizeObserver is not supported
      window.addEventListener('resize', () => {
        const wasMobile = state.isMobileView;
        checkViewportSize();
        if (wasMobile !== state.isMobileView) {
          createPaginationLinks(); // Only update if view type changed
        }
      });
      return;
    }

    // Create ResizeObserver instance
    const resizeObserver = new ResizeObserver(() => {
      const wasMobile = state.isMobileView;
      checkViewportSize();
      if (wasMobile !== state.isMobileView) {
        createPaginationLinks(); // Only update if view type changed
      }
    });

    // Start observing document body
    resizeObserver.observe(document.body);
  };

  // Create paging UI
  const initPaging = () => {
    if (!options.paging && !options.forcePaging) return;

    // Create top and bottom pagination
    const pagingTop = document.createElement('div');
    pagingTop.className = 'paging';

    const pagingBottom = document.createElement('div');
    pagingBottom.className = 'paging';

    // Insert pagination containers before and after the content
    const contentContainer = props.container.querySelector('ul.main');
    if (contentContainer) {
      contentContainer.insertAdjacentElement('beforebegin', pagingTop);
      contentContainer.insertAdjacentElement('afterend', pagingBottom);
    }

    // Add prev buttons
    const prevBtnTop = createNavigationButton('prev');
    const prevBtnBottom = createNavigationButton('prev');
    pagingTop.appendChild(prevBtnTop);
    pagingBottom.appendChild(prevBtnBottom);

    // Add paging holders
    const pagingHolderTop = document.createElement('div');
    pagingHolderTop.className = 'paging-holder';

    const pagingHolderBottom = document.createElement('div');
    pagingHolderBottom.className = 'paging-holder';

    pagingTop.appendChild(pagingHolderTop);
    pagingBottom.appendChild(pagingHolderBottom);

    // Add next buttons
    const nextBtnTop = createNavigationButton('next');
    const nextBtnBottom = createNavigationButton('next');
    pagingTop.appendChild(nextBtnTop);
    pagingBottom.appendChild(nextBtnBottom);

    // Add clearfix
    const clearfixTop = document.createElement('div');
    clearfixTop.className = 'clearfix';
    pagingTop.appendChild(clearfixTop);

    const clearfixBottom = document.createElement('div');
    clearfixBottom.className = 'clearfix';
    pagingBottom.appendChild(clearfixBottom);

    // Store references to paging holders
    state.pagingHolders = [pagingHolderTop, pagingHolderBottom];

    // Create pagination links
    createPaginationLinks();
    updateButtonStates();

    // Set up resize observer to handle responsive behavior
    setupResizeObserver();
  };

  // Create navigation button (prev/next)
  const createNavigationButton = (type: 'prev' | 'next') => {
    const button = document.createElement('button');
    button.className = `${type} sport-color-bg sport-color-hover-bg-darken`;

    const iconClass = 'arrow-listing';

    const svgPath =
      type === 'prev'
        ? 'M0,12.8c0-0.2,0.1-0.3,0.2-0.4l0,0L11.7,0.2l0,0 C11.8,0.1,12,0,12.2,0c0.4,0,0.6,0.3,0.6,0.6c0,0.2-0.1,0.3-0.2,0.4l0,0L1.5,12.8l11.1,11.7l0,0c0.1,0.1,0.2,0.3,0.2,0.4 c0,0.4-0.3,0.6-0.6,0.6c-0.2,0-0.3-0.1-0.5-0.2l0,0L0.2,13.2l0,0C0.1,13.1,0,13,0,12.8z'
        : 'M12.8,12.8c0,0.2-0.1,0.3-0.2,0.4l0,0L1.1,25.4l0,0 c-0.1,0.1-0.3,0.2-0.5,0.2C0.3,25.6,0,25.3,0,25c0-0.2,0.1-0.3,0.2-0.4l0,0l11.1-11.7L0.2,1.1l0,0C0.1,1,0,0.8,0,0.6 C0,0.3,0.3,0,0.6,0C0.8,0,1,0.1,1.1,0.2l0,0l11.5,12.2l0,0C12.7,12.5,12.8,12.6,12.8,12.8z';

    button.innerHTML = `
            <div class="${iconClass}">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12.8 25.6">
                    <path fill="#FFFFFF" d="${svgPath}"/>
                </svg>
            </div>
        `;

    if (
      (type === 'prev' && state.currentPage <= 1) ||
      (type === 'next' && state.currentPage >= state.totalPages)
    ) {
      button.setAttribute('disabled', 'disabled');
    } else {
      button.classList.add(`show-${type}`);
    }

    button.addEventListener('click', () => {
      const direction = type === 'next' ? 'next' : 'previous';

      if (type === 'next' && state.currentPage < state.totalPages) {
        goToPage(state.currentPage + 1, direction);
      } else if (type === 'prev' && state.currentPage > 1) {
        goToPage(state.currentPage - 1, direction);
      }
    });

    return button;
  };

  // Create pagination links with proper sliding window logic
  const createPaginationLinks = () => {
    state.pagingHolders.forEach((holder) => {
      holder.innerHTML = '';

      const totalPages = allChapters.length;
      // Use different pagingBy value based on viewport size
      const pagingBy = state.isMobileView ? options.mobilePageLimit : options.pagingBy;
      const currentPage = state.currentPage;

      // Helper function to create page links
      const createPageLink = (pageNum: number) => {
        const chapter = allChapters[pageNum - 1];
        const chapterId = chapter.getAttribute('data-id') || '';

        const link = document.createElement('a');
        link.href = '#';
        link.setAttribute('data-id', chapterId);

        if (pageNum === currentPage) {
          link.className = 'active sport-color';
        } else {
          link.className = 'sport-color-hover';
        }

        // Set link text based on position
        if (pageNum === 1) {
          link.textContent = 'úvod';
        } else if (chapterId === 'related') {
          link.textContent = 'více';
        } else {
          link.textContent = (pageNum - 1).toString();
        }

        link.addEventListener('click', (e) => {
          e.preventDefault();
          goToPage(pageNum, pageNum > currentPage ? 'next' : 'previous');
        });

        return link;
      };

      // Helper function to create clickable ellipsis - modified to go to first/last page
      const createEllipsis = (isStart: boolean) => {
        const link = document.createElement('a');
        link.href = '#';
        // Add base classes for all ellipses
        link.className = 'threeDots ellipsis sport-color-hover';

        // Add direction-specific class (prev for left, next for right)
        if (isStart) {
          link.classList.add('prev');
        } else {
          link.classList.add('next');
        }

        link.textContent = '...';

        // Left ellipsis goes to first page, right ellipsis to last page
        const targetPage = isStart ? 1 : totalPages;

        link.addEventListener('click', (e) => {
          e.preventDefault();
          goToPage(targetPage, isStart ? 'previous' : 'next');
        });

        return link;
      };

      // If few pages, just show all of them
      if (totalPages <= pagingBy) {
        for (let i = 1; i <= totalPages; i++) {
          holder.appendChild(createPageLink(i));
        }
        return;
      }

      // Calculate window size and position for sliding window
      let startPage, endPage;

      // First set of pages - showing initial window
      if (currentPage <= Math.ceil(pagingBy / 2)) {
        startPage = 1;
        endPage = Math.min(pagingBy, totalPages);

        // Add all pages in window
        for (let i = startPage; i <= endPage; i++) {
          holder.appendChild(createPageLink(i));
        }

        // Add ellipsis if there are more pages
        if (endPage < totalPages) {
          holder.appendChild(createEllipsis(false)); // false = end ellipsis (to last page)
        }

        return;
      }

      // Last set of pages - showing final window
      if (currentPage >= totalPages - Math.floor(pagingBy / 2)) {
        startPage = Math.max(1, totalPages - pagingBy + 1);
        endPage = totalPages;

        // Add ellipsis if needed
        if (startPage > 1) {
          holder.appendChild(createEllipsis(true)); // true = start ellipsis (to first page)
        }

        // Add all pages in window
        for (let i = startPage; i <= endPage; i++) {
          holder.appendChild(createPageLink(i));
        }

        return;
      }

      // Middle case - we're somewhere in the middle of pagination
      // Center the window around the current page
      const halfWindow = Math.floor(pagingBy / 2);
      startPage = currentPage - halfWindow;
      endPage = currentPage + halfWindow - (pagingBy % 2 === 0 ? 1 : 0);

      // Ensure we don't go out of bounds
      if (startPage < 1) {
        startPage = 1;
        endPage = Math.min(pagingBy, totalPages);
      }

      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, totalPages - pagingBy + 1);
      }

      // Add ellipsis at start if needed
      if (startPage > 1) {
        holder.appendChild(createEllipsis(true)); // true = start ellipsis (to first page)
      }

      // Add all pages in window
      for (let i = startPage; i <= endPage; i++) {
        holder.appendChild(createPageLink(i));
      }

      // Add ellipsis at end if needed
      if (endPage < totalPages) {
        holder.appendChild(createEllipsis(false)); // false = end ellipsis (to last page)
      }
    });
  };

  // Navigate to specific page
  const goToPage = (pageNum: number, direction = 'next') => {
    if (
      pageNum < 1 ||
      pageNum > state.totalPages ||
      pageNum === state.currentPage ||
      state.isLoading
    ) {
      return;
    }

    state.previousPage = state.currentPage;
    state.isLoading = true;

    // Get elements
    const oldChapter = allChapters[state.currentPage - 1];
    const newChapter = allChapters[pageNum - 1];

    // Hide old chapter
    if (options.fadeOut > 0) {
      oldChapter.style.transition = `opacity ${options.fadeOut}ms`;
      oldChapter.style.opacity = '0';

      setTimeout(() => {
        oldChapter.style.display = 'none';
        oldChapter.classList.remove('active');

        // Show new chapter
        showNewChapter();
      }, options.fadeOut);
    } else {
      oldChapter.style.display = 'none';
      oldChapter.classList.remove('active');
      showNewChapter();
    }

    // Show new chapter with optional fade
    function showNewChapter() {
      newChapter.classList.add('active');
      newChapter.style.display = 'block';

      if (options.fadeIn > 0) {
        newChapter.style.opacity = '0';

        setTimeout(() => {
          newChapter.style.transition = `opacity ${options.fadeIn}ms`;
          newChapter.style.opacity = '1';

          setTimeout(finishTransition, options.fadeIn);
        }, 10);
      } else {
        newChapter.style.opacity = '1';
        finishTransition();
      }
    }

    // Update state after transition
    function finishTransition() {
      state.currentPage = pageNum;
      state.isLoading = false;

      updateUI();
      trackPageChange(direction);
      updateURL();
    }
  };

  // Update navigation and UI
  const updateUI = () => {
    // Update pagination links
    createPaginationLinks();

    // Update button states
    updateButtonStates();
  };

  // Update navigation button states
  const updateButtonStates = () => {
    const prevButtons = props.container.querySelectorAll('button.prev');
    const nextButtons = props.container.querySelectorAll('button.next');

    prevButtons.forEach((btn) => {
      if (state.currentPage <= 1) {
        btn.classList.remove('show-prev');
        btn.setAttribute('disabled', 'disabled');
      } else {
        btn.classList.add('show-prev');
        btn.removeAttribute('disabled');
      }
    });

    nextButtons.forEach((btn) => {
      const withoutRelatedArticles =
        state.currentPage === regularChaptersCount &&
        !options.showRecentArticles &&
        relatedChapterIndex !== -1;

      const isLastPage = state.currentPage >= state.totalPages;

      if (isLastPage || withoutRelatedArticles) {
        btn.classList.remove('show-next', 'sport-color-hover-bg-darken');
        btn.setAttribute('disabled', 'disabled');
      } else {
        btn.classList.add('show-next', 'sport-color-hover-bg-darken');
        btn.removeAttribute('disabled');
      }
    });
  };

  // Track page change for analytics
  const trackPageChange = (direction: string) => {
    // Get part number for tracking
    let part: number | string;

    // Format part number similar to jQuery version
    if (state.currentPage === 1) {
      part = 1;
    } else if (allChapters[state.currentPage - 1].getAttribute('data-id') === 'related') {
      part = 'více';
    } else {
      part = state.currentPage - 1; // Subtract 1 to match the displayed number
    }

    // Call the onPageChange callback if provided
    if (props.onPageChange) {
      props.onPageChange(direction, part);
    }

    // Reload ads if available - using safe type checking
    if (typeof window['__cncAdsReload'] !== 'undefined') {
      if (window['__cncAdsQueue'] && typeof window['__cncAdsQueue'].push === 'function') {
        window['__cncAdsQueue'].push(function () {
          if (window['__cncAdsReload']) window['__cncAdsReload']();
        });
      }
    }

    // Update analytics page view - using safe array access
    const pagePath = location.pathname + window.location.search;
    const pageTitle = document.title;

    if (window['dataLayer'] && Array.isArray(window['dataLayer'])) {
      window['dataLayer'].push({
        event: 'ga.pageview',
        pagePath: pagePath,
        pageTitle: pageTitle,
      });
    }
  };

  // Update URL with chapter parameter
  const updateURL = () => {
    const chapter = allChapters[state.currentPage - 1];
    const chapterId = chapter.getAttribute('data-id');

    const url = new URL(window.location.href);

    if (state.currentPage === 1) {
      // Remove parameter for first page
      url.searchParams.delete('kapitola');
    } else if (chapterId === 'related') {
      url.searchParams.set('kapitola', 'related');
    } else if (chapterId) {
      url.searchParams.set('kapitola', chapterId);
    } else {
      url.searchParams.set('kapitola', (state.currentPage - 1).toString()); // Use displayed number
    }

    window.history.replaceState({}, '', url.toString());
  };

  // Initialize
  const init = () => {
    // Check viewport size immediately on init
    checkViewportSize();

    // Hide all chapters except current
    allChapters.forEach((chapter, index) => {
      if (index + 1 === state.currentPage) {
        chapter.style.display = 'block';
        chapter.style.opacity = '1';
        chapter.classList.add('active');
      } else {
        chapter.style.display = 'none';
        chapter.style.opacity = '0';
        chapter.classList.remove('active');
      }
    });

    // Initialize paging
    initPaging();

    // Add keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft' && state.currentPage > 1) {
        goToPage(state.currentPage - 1, 'previous');
      } else if (e.key === 'ArrowRight' && state.currentPage < state.totalPages) {
        goToPage(state.currentPage + 1, 'next');
      }
    });

    // Call onInit callback if provided
    if (props.onInit) {
      props.onInit();
    }
  };

  // Run initialization
  init();

  // Return public API
  return {
    goToPage,
    getCurrentPage: () => state.currentPage,
    getTotalPages: () => state.totalPages,
    navigatePrev: () => {
      if (state.currentPage > 1) {
        goToPage(state.currentPage - 1, 'previous');
      }
    },
    navigateNext: () => {
      if (state.currentPage < state.totalPages) {
        goToPage(state.currentPage + 1, 'next');
      }
    },
  };
};

// Initialize ArticleListed components
export const initArticleListed = () => {
  // Auto-initialization is handled by the script in article.twig
  // This function is kept for backward compatibility
  const containers = Array.from(document.querySelectorAll('.article-listed')) as HTMLElement[];

  containers.forEach((container) => {
    createArticleListed({ container });
  });
};
